﻿import type {SectionMeasurements} from "../models/section-measurements.model";
import type {RouteMeasurements} from "../models/route-measurements.model";
import type {BaseMeasurements} from "../models/base-measurements.model";
import {
    MEASUREMENT_FIELD_CURRENT_DURATION, MEASUREMENT_FIELD_CURRENT_SPEED,
    MEASUREMENT_FIELD_DISTANCE, MEASUREMENT_FIELD_DELAY_DURATION,
    MEASUREMENT_FIELD_DELAY_FACTOR, MEASUREMENT_FIELD_OPTIMAL_DURATION, MEASUREMENT_FIELD_OPTIMAL_SPEED,
    MEASUREMENT_FIELD_ROUTE_ID,
    MEASUREMENT_FIELD_SPEED_DIFFERENCE, MEASUREMENT_FIELD_TIMESTAMP, MEASUREMENT_FIELD_TYPICAL_DURATION,
    MEASUREMENT_FIELD_TYPICAL_SPEED,
    MEASUREMENT_FIELD_ROUTE_NAME,
    MEASUREMENT_FIELD_END_GATE_NAME,
    MEASUREMENT_FIELD_START_GATE_NAME
} from "../constants/measurement-table-constants";
import type {SectionMeasurement } from "../types/measurements/section-measurement.type";
import type {Section} from "../types/projectItems/section.type";
import type {Gate} from "../types/projectItems/gate.type";
import type {RouteGate} from "../types/projectItems/route-gate.type";
import type {Route} from "../types/projectItems/route.type";

const METERS_PER_SECOND_TO_KM_PER_HOUR = 3.6;

export function parseSectionMeasurements(
    gates: Gate[], 
    routeGates: RouteGate[],
    sections: Section[], 
    measurements: SectionMeasurement[]
) : SectionMeasurements[] {
    const gateMap = new Map<string, Gate>(
        gates.map(gate => [gate.id, gate])
    );
    const routeGateMap = new Map<string, RouteGate>(
        routeGates.map(routeGate => [routeGate.id, routeGate])
    );
    const sectionMap = new Map<string, Section>(
        sections.map(section => [section.id, section])
    );
    
    return measurements.map((sectionMeasurement) => { 
        return mapSectionMeasurements(gateMap, routeGateMap, sectionMap, sectionMeasurement) 
    })
    .sort((a, b) => {
        const timestampDifference = new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
        if (timestampDifference !== 0)
            return timestampDifference;

        return a.startGateName.localeCompare(b.startGateName);
    });
}


export function parseRouteMeasurements(
    sections: Section[],
    routes: Route[], 
    measurements: SectionMeasurement[]
) : RouteMeasurements[] {
    const sectionMap = new Map<string, Section>(
        sections.map(section => [section.id, section])
    );
    const sectionsByRouteId = groupBy(
        sections, section => section.routeId
    );
    const measurementsByRoute = groupBy(
        measurements, measurement => sectionMap.get(measurement.sectionId).routeId
    );
    
    return routes.flatMap((route) => {
        const routeMeasurements = measurementsByRoute.get(route.id) ?? []; 
        const routeMeasurementsByTimestamp = groupBy(
            routeMeasurements, 
            measurement => measurement.moment
        );

        return Array.from(routeMeasurementsByTimestamp.values()).map((measurements) => {
            return mapRouteMeasurements(route, sectionsByRouteId.get(route.id), measurements);
        });
    })
    .sort((a, b) => {
        const timestampDifference = new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
        if (timestampDifference !== 0)
            return timestampDifference;

        return a.routeName.localeCompare(b.routeName);
    });
}

function mapSectionMeasurements(
    gateMap: Map<string, Gate>, 
    routeGateMap: Map<string, RouteGate>,
    sectionMap: Map<string, Section>,
    sectionMeasurement: SectionMeasurement
) : SectionMeasurements {
    const section = sectionMap.get(sectionMeasurement.sectionId);
    
    const routeGateFrom = routeGateMap.get(section.routeGateFromId);
    const gateFrom = gateMap.get(routeGateFrom.gateId);

    const routeGateTo = routeGateMap.get(section.routeGateToId);
    const gateTo = gateMap.get(routeGateTo.gateId);
    
    return {
        [MEASUREMENT_FIELD_START_GATE_NAME]: gateFrom.name,
        [MEASUREMENT_FIELD_END_GATE_NAME]: gateTo.name,

        ...mapBaseMeasurement(
            section.routeId,
            sectionMeasurement.moment,
            section.distance,
            section.freeFlowDuration,
            sectionMeasurement.typicalDuration,
            sectionMeasurement.duration
        )
    }
}

function mapRouteMeasurements(
    route: Route, 
    sections: Section[],
    sectionMeasurements: SectionMeasurement[]
) : RouteMeasurements {
    let routeDistance = 0;
    let routeFreeFlowDuration = 0;
    sections.forEach(section => {
        // accumulate distance and freeFlowDuration of all sections in this route
        routeDistance += section.distance;
        routeFreeFlowDuration += section.freeFlowDuration;
    })
    
    let routeTypicalDuration = 0;
    let routeDuration = 0;
    sectionMeasurements.forEach(sectionMeasurement => {
        // accumulate duration and typicalDuration of all sectionMeasurements for the route's sections
        routeTypicalDuration += sectionMeasurement.typicalDuration;
        routeDuration += sectionMeasurement.duration;
    })
    
    return {
        [MEASUREMENT_FIELD_ROUTE_NAME]: route.name,
        ...mapBaseMeasurement(
            route.id,
            sectionMeasurements[0].moment, 
            routeDistance, 
            routeFreeFlowDuration, 
            routeTypicalDuration, 
            routeDuration
        )
    }
}

function mapBaseMeasurement(
    routeId: string,
    moment: string,
    distance: number,
    freeFlowDuration: number,
    typicalDuration: number,
    duration: number
) : BaseMeasurements {

    const optimalVelocity = safeDivide(distance, freeFlowDuration) * METERS_PER_SECOND_TO_KM_PER_HOUR;
    const typicalVelocity = safeDivide(distance, typicalDuration) * METERS_PER_SECOND_TO_KM_PER_HOUR;
    const currentVelocity = safeDivide(distance, duration) * METERS_PER_SECOND_TO_KM_PER_HOUR;

    return {
        [MEASUREMENT_FIELD_ROUTE_ID]: routeId,
        [MEASUREMENT_FIELD_TIMESTAMP]: new Date(moment),
        [MEASUREMENT_FIELD_DISTANCE]: distance,

        [MEASUREMENT_FIELD_OPTIMAL_DURATION]: freeFlowDuration,
        [MEASUREMENT_FIELD_OPTIMAL_SPEED]: optimalVelocity,

        [MEASUREMENT_FIELD_TYPICAL_DURATION]: typicalDuration,
        [MEASUREMENT_FIELD_TYPICAL_SPEED]: typicalVelocity,

        [MEASUREMENT_FIELD_CURRENT_DURATION]: duration,
        [MEASUREMENT_FIELD_CURRENT_SPEED]: currentVelocity,

        [MEASUREMENT_FIELD_DELAY_FACTOR]: safeDivide(100 * (duration - typicalDuration), typicalDuration),
        [MEASUREMENT_FIELD_DELAY_DURATION]: duration - typicalDuration,

        [MEASUREMENT_FIELD_SPEED_DIFFERENCE]: currentVelocity - typicalVelocity
    }
}

function groupBy<TItem, TKey>(
    items: TItem[],
    keySelector: (item: TItem) => TKey
): Map<TKey, TItem[]> {
    return items.reduce((groups, item) => {
        const key = keySelector(item);
        const group = groups.get(key);

        if (group) {
            group.push(item);
        } else {
            groups.set(key, [item]);
        }

        return groups;
    }, new Map<TKey, TItem[]>());
}

function safeDivide(numerator: number, denominator: number): number {
    return denominator === 0 ? 0 : numerator / denominator;
}

